import type { ChangeEvent, KeyboardEvent } from 'react';

import {
  FileOutlineIcon,
  FileUploader,
  IconButton,
  InputDescription,
  Modal,
  Note,
  PlusIcon,
  PrimaryButton,
  Radio,
  SecondaryButton,
  SelectionInputLayout,
  Spinner,
  TextInput,
  TrashIcon,
  WarningIcon,
} from '@movingimage-evp/mi-ui-component-library';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import styles from './dictionary-words-modal.module.css';

export const MAX_DICTIONARY_ENTRIES = 1000;
export const MAX_DICTIONARY_ENTRY_LENGTH = 300;

export type DictionaryWord = {
  word: string;
  soundsLike: string[];
};

type DictionaryWordsModalProps = {
  isOpen: boolean;
  initialWord?: DictionaryWord;
  existingWords: DictionaryWord[];
  onClose: () => void;
  onConfirm: (words: DictionaryWord[]) => Promise<boolean | void> | boolean | void;
};

type AddMethod = 'manual' | 'csv';

type WordEntry = {
  id: string;
  word: string;
  soundsLike: string;
};

export function DictionaryWordsModal({
  isOpen,
  initialWord,
  existingWords,
  onClose,
  onConfirm,
}: DictionaryWordsModalProps) {
  const isEditMode = initialWord !== undefined;

  const createInitialWords = useCallback((): WordEntry[] => {
    if (initialWord) {
      return [{ id: crypto.randomUUID(), word: initialWord.word, soundsLike: initialWord.soundsLike.join(', ') }];
    }

    return [{ id: crypto.randomUUID(), word: '', soundsLike: '' }];
  }, [initialWord]);

  const [words, setWords] = useState<WordEntry[]>(createInitialWords);
  const [addMethod, setAddMethod] = useState<AddMethod>('manual');
  const [isNoteDismissed, setIsNoteDismissed] = useState(false);
  const [isCsvLoading, setIsCsvLoading] = useState(false);
  const [csvParsed, setCsvParsed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    if (isOpen) {
      setWords(createInitialWords());
      setAddMethod('manual');
      setIsNoteDismissed(false);
      setCsvParsed(false);
      setErrorMessage(undefined);
    }
  }, [isOpen, createInitialWords]);

  const resetState = () => {
    setWords([{ id: crypto.randomUUID(), word: '', soundsLike: '' }]);
    setAddMethod('manual');
    setIsNoteDismissed(false);
    setCsvParsed(false);
    setErrorMessage(undefined);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleConfirm = async () => {
    // Process words: filter empty entries, split sounds like by comma
    const processedWords: DictionaryWord[] = words
      .filter((entry) => entry.word.trim() !== '')
      .map((entry) => ({
        word: entry.word.trim(),
        soundsLike: entry.soundsLike
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s !== ''),
      }));

    const result = await onConfirm(processedWords);

    if (result !== false) {
      handleClose();
    }
  };

  const handleWordChange = (id: string, field: 'word' | 'soundsLike', value: string) => {
    setWords((state) => state.map((entry) => (entry.id === id ? { ...entry, [field]: value } : entry)));
    setErrorMessage(undefined);
  };

  const handleAddWord = () => {
    setWords((state) => [...state, { id: crypto.randomUUID(), word: '', soundsLike: '' }]);
  };

  const handleRemoveWord = (id: string) => {
    if (words.length > 1) {
      setWords((state) => state.filter((entry) => entry.id !== id));
    }
  };

  const handleUploadFile = (files: File | File[]) => {
    const file = Array.isArray(files) ? files[0] : files;

    setErrorMessage(undefined);

    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage(t('manager.webcastSetup.language.inputs.customDictionary.modal.csvUpload.hint.size'));

      return;
    }

    const fileReader = new FileReader();

    setIsCsvLoading(true);

    fileReader.onload = (event) => {
      parseCsvItems(event?.target?.result as string);
      setIsCsvLoading(false);
    };

    fileReader.onerror = () => {
      setErrorMessage(t('manager.webcastSetup.language.inputs.customDictionary.modal.csvUpload.hint.readError'));
      setIsCsvLoading(false);
    };

    fileReader.readAsText(file);
  };

  const parseCsvItems = (value?: string) => {
    if (!value?.trim()) {
      return setErrorMessage(t('manager.webcastSetup.language.inputs.customDictionary.modal.csvUpload.hint.empty'));
    }

    const lines = value
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    const parsed: WordEntry[] = lines.map((line, index) => {
      const semicolonIndex = line.indexOf(';');

      if (semicolonIndex === -1) {
        return { id: `csv-${index}`, word: line.trim(), soundsLike: '' };
      }

      const word = line.substring(0, semicolonIndex).trim();
      const soundsLike = line.substring(semicolonIndex + 1).trim();

      return { id: `csv-${index}`, word, soundsLike };
    });

    const validEntries = parsed.filter((entry) => entry.word !== '');

    if (validEntries.length === 0) {
      return setErrorMessage(t('manager.webcastSetup.language.inputs.customDictionary.modal.csvUpload.hint.empty'));
    }

    if (validEntries.length > MAX_DICTIONARY_ENTRIES) {
      return setErrorMessage(
        t('manager.webcastSetup.language.inputs.customDictionary.modal.csvUpload.hint.maxEntries', {
          count: validEntries.length,
        })
      );
    }

    const tooLongEntry = validEntries.find(
      (entry) =>
        entry.word.length > MAX_DICTIONARY_ENTRY_LENGTH ||
        entry.soundsLike.split(',').some((s) => s.trim().length > MAX_DICTIONARY_ENTRY_LENGTH)
    );

    if (tooLongEntry) {
      return setErrorMessage(
        t('manager.webcastSetup.language.inputs.customDictionary.modal.csvUpload.hint.maxCharacters', {
          word: `${tooLongEntry.word.slice(0, 30)}…`,
        })
      );
    }

    setWords(validEntries);
    setCsvParsed(true);
  };

  const newWordIndexRef = useRef<number | null>(null);
  const wordsListRef = useRef<HTMLDivElement>(null);

  // Focus the new word input when a new word is added
  useEffect(() => {
    if (newWordIndexRef.current !== null) {
      const input = wordsListRef.current?.querySelector<HTMLInputElement>(
        `[data-testid="dictionary-word-input-${newWordIndexRef.current}"]`
      );

      input?.focus();
      newWordIndexRef.current = null;
    }
  }, [words.length]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !isEditMode) {
      event.preventDefault();
      newWordIndexRef.current = words.length;
      handleAddWord();
    }
  };

  const duplicateIds = useMemo(() => {
    const ids = new Set<string>();
    const existingSet = new Set(
      existingWords
        .filter((w) => !(isEditMode && w.word.toLowerCase() === initialWord?.word.toLowerCase()))
        .map((w) => w.word.toLowerCase().trim())
    );

    const seen = new Set<string>();

    for (const entry of words) {
      const key = entry.word.toLowerCase().trim();
      if (key === '') continue;

      if (existingSet.has(key) || seen.has(key)) {
        ids.add(entry.id);
      }
      seen.add(key);
    }

    return ids;
  }, [words, existingWords, isEditMode, initialWord]);

  const validWords = words.filter((entry) => entry.word.trim() !== '');
  const submitDisabled = validWords.length === 0 || duplicateIds.size > 0;

  return (
    <Modal
      data-testid="dictionary-words-modal"
      contentProps={{ className: styles.modalContent }}
      title={
        isEditMode
          ? t('manager.webcastSetup.language.inputs.customDictionary.modal.titleEdit')
          : t('manager.webcastSetup.language.inputs.customDictionary.modal.title')
      }
      isOpen={isOpen}
      onClose={handleClose}
      footer={
        <div className={styles.modalFooter}>
          <SecondaryButton data-testid="dictionary-words-modal-cancel-button" onClick={handleClose}>
            {t('manager.webcastSetup.language.inputs.customDictionary.modal.footer.cancel')}
          </SecondaryButton>

          <PrimaryButton
            data-testid="dictionary-words-modal-submit-button"
            disabled={submitDisabled}
            onClick={handleConfirm}
          >
            {isEditMode
              ? t('manager.webcastSetup.language.inputs.customDictionary.modal.footer.update')
              : t('manager.webcastSetup.language.inputs.customDictionary.modal.footer.confirm')}
          </PrimaryButton>
        </div>
      }
    >
      {!isEditMode && !csvParsed && (
        <div className={styles.addMethodWrapper}>
          <SelectionInputLayout
            checked={addMethod === 'manual'}
            inputElement={
              <Radio
                data-testid="add-dictionary-words-manual"
                name="add-dictionary-words-method"
                value="manual"
                checked={addMethod === 'manual'}
                onChange={() => setAddMethod('manual')}
              >
                {t('manager.webcastSetup.language.inputs.customDictionary.modal.addMethod.manual')}
              </Radio>
            }
          />

          <SelectionInputLayout
            checked={addMethod === 'csv'}
            inputElement={
              <Radio
                data-testid="add-dictionary-words-csv"
                name="add-dictionary-words-method"
                value="csv"
                checked={addMethod === 'csv'}
                onChange={() => setAddMethod('csv')}
              >
                {t('manager.webcastSetup.language.inputs.customDictionary.modal.addMethod.csv')}
              </Radio>
            }
          />
        </div>
      )}

      {addMethod === 'csv' && !csvParsed && (
        <div className={styles.fileUploaderWrapper}>
          {isCsvLoading && <Spinner className={styles.fileUploaderSpinner} />}

          <FileUploader
            key={errorMessage}
            acceptedFormats=".csv"
            icon={<FileOutlineIcon />}
            onChange={handleUploadFile}
            testIdPrefix="dictionary-csv-"
            additionalUploadInformation={[
              t('manager.webcastSetup.language.inputs.customDictionary.modal.csvUpload.hint.size'),
              t('manager.webcastSetup.language.inputs.customDictionary.modal.csvUpload.hint.soundsLikeStructure'),
              t('manager.webcastSetup.language.inputs.customDictionary.modal.csvUpload.hint.soundsLikeStart'),
              t('manager.webcastSetup.language.inputs.customDictionary.modal.csvUpload.hint.soundsLikeSemicolon'),
              t('manager.webcastSetup.language.inputs.customDictionary.modal.csvUpload.hint.soundsLikeComma'),
              t('manager.webcastSetup.language.inputs.customDictionary.modal.csvUpload.hint.exampleTitle'),
              t('manager.webcastSetup.language.inputs.customDictionary.modal.csvUpload.hint.example1'),
              t('manager.webcastSetup.language.inputs.customDictionary.modal.csvUpload.hint.example2'),
            ]}
          >
            {errorMessage && (
              <div data-testid="csv-upload-error-message" className={styles.resultInfo}>
                <WarningIcon />
                {errorMessage}
              </div>
            )}
          </FileUploader>
        </div>
      )}

      {(addMethod === 'manual' || isEditMode || csvParsed) && (
        <>
          {!isEditMode && !csvParsed && !isNoteDismissed && (
            <Note variant="primary" className={styles.infoBox} onClose={() => setIsNoteDismissed(true)}>
              {t('manager.webcastSetup.language.inputs.customDictionary.modal.infoBox.text')}
              <br />
              <br />
              <strong>{t('manager.webcastSetup.language.inputs.customDictionary.modal.infoBox.wordLimit')}</strong>
            </Note>
          )}

          <div className={styles.wordsContainer}>
            <div ref={wordsListRef} className={styles.wordsList}>
              {words.map((entry, index) => (
                <div key={entry.id} className={styles.wordRow}>
                  <InputDescription
                    label={t('manager.webcastSetup.language.inputs.customDictionary.modal.dictionaryWord.label')}
                    helpMessage={t('manager.webcastSetup.language.inputs.customDictionary.modal.dictionaryWord.hint')}
                    errorMessage={
                      duplicateIds.has(entry.id)
                        ? t('manager.webcastSetup.language.inputs.customDictionary.modal.duplicateWordError')
                        : undefined
                    }
                  >
                    <TextInput
                      data-testid={`dictionary-word-input-${index}`}
                      value={entry.word}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleWordChange(entry.id, 'word', e.target.value)
                      }
                      onKeyDown={handleKeyDown}
                      placeholder={t(
                        'manager.webcastSetup.language.inputs.customDictionary.modal.dictionaryWord.placeholder'
                      )}
                    />
                  </InputDescription>

                  <div className={styles.soundsLikeWrapper}>
                    <InputDescription
                      label={t('manager.webcastSetup.language.inputs.customDictionary.modal.soundsLike.label')}
                      helpMessage={t('manager.webcastSetup.language.inputs.customDictionary.modal.soundsLike.hint')}
                    >
                      <TextInput
                        data-testid={`sounds-like-input-${index}`}
                        value={entry.soundsLike}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleWordChange(entry.id, 'soundsLike', e.target.value)
                        }
                        onKeyDown={handleKeyDown}
                        placeholder={t(
                          'manager.webcastSetup.language.inputs.customDictionary.modal.soundsLike.placeholder'
                        )}
                      />
                    </InputDescription>

                    {!isEditMode && words.length > 1 && (
                      <IconButton
                        naked
                        small
                        data-testid={`remove-word-button-${index}`}
                        aria-label={t('manager.webcastSetup.language.inputs.customDictionary.list.removeWord')}
                        onClick={() => handleRemoveWord(entry.id)}
                      >
                        <TrashIcon />
                      </IconButton>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {!isEditMode && !csvParsed && (
            <IconButton
              data-testid="add-word-button"
              aria-label={t('manager.webcastSetup.language.inputs.customDictionary.modal.addWordButton')}
              naked
              medium
              className={styles.addWordButton}
              onClick={handleAddWord}
            >
              <PlusIcon />
              {t('manager.webcastSetup.language.inputs.customDictionary.modal.addWordButton')}
            </IconButton>
          )}

          {errorMessage && (
            <div data-testid="dictionary-error-message" className={styles.resultInfo}>
              <WarningIcon />
              {errorMessage}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
